import { useCallback, useMemo, useState } from "react";
import { Navigate } from "react-router";
import _ from "lodash";

import { URLS } from "config/urls";
import theme from "theme";
import { colors } from "theme/palette";

import PageHeader from "components/PageHeader";
import Paper from "components/Paper";
import Skeleton from "components/Skeleton";
import Stack from "components/Stack";
import Switch from "components/Switch";
import Text from "components/Text";
import { usePageTitle } from "components/usePageTitle";

import DealAndChecklistPendingInvitationsDialog from "pages/Projects/DealAndChecklistPendingInvitationsDialog";

import { EApplicationCreatedByNonPayingCustomerStatus } from "entities/Application/constants";
import { useApplicationsCreatedByNonPayingCustomer } from "entities/Application/sdk";
import { useSignedUser } from "entities/Auth/sdk";
import DealsGrid from "entities/Deal/components/Grid";
import {
  useChecklistsAccessibleToNonPayingCustomer,
  useDealsAccessibleToNonPayingCustomer,
} from "entities/Deal/sdk";
import { MemoSignatureStatus } from "entities/Memo/components/Sections/MemoESignatureDrawer/constants";
import { useMemoSignatures } from "entities/Memo/sdk";

import ApplicationBox from "./ApplicationBox";
import BookADemoBox from "./BookADemoBox";
import ChecklistBox from "./ChecklistBox";
import MemoSignatureBox from "./MemoSignatureBox";

const CollaboratorDashboard = () => {
  const [includeSubmittedApplications, setIncludeSubmittedApplications] =
    useState<boolean>(true);
  const [includeSignedMemoSignatures, setIncludeSignedMemoSignatures] =
    useState<boolean>(true);

  const { data: applications } = useApplicationsCreatedByNonPayingCustomer();

  const { data: memoSignatures } = useMemoSignatures();

  const { data: user } = useSignedUser();

  usePageTitle("Builders Patch: Collaborators Dashboard");

  const shownApplications = useMemo(() => {
    if (includeSubmittedApplications) {
      return applications;
    }
    return applications?.filter(
      (application) =>
        application.status ===
        EApplicationCreatedByNonPayingCustomerStatus.DRAFT
    );
  }, [applications, includeSubmittedApplications]);

  const shownMemoSignatures = useMemo(() => {
    if (includeSignedMemoSignatures) {
      return memoSignatures;
    }
    return memoSignatures?.filter(
      (signature) => signature.status !== MemoSignatureStatus.APPLIED
    );
  }, [memoSignatures, includeSignedMemoSignatures]);

  const {
    data: checklists,
    isLoading: areChecklistsLoading,
    isValidating: areChecklistsValidating,
    mutate: mutateChecklists,
  } = useChecklistsAccessibleToNonPayingCustomer();
  const {
    data: deals,
    isLoading: areDealsLoading,
    mutate: mutateDeals,
  } = useDealsAccessibleToNonPayingCustomer();

  const handleOnIncludeSubmittedApplicationsClick = useCallback(() => {
    setIncludeSubmittedApplications((prevState) => !prevState);
  }, []);

  const handleOnIncludeSignedMemoSignaturesClick = useCallback(() => {
    setIncludeSignedMemoSignatures((prevState) => !prevState);
  }, []);

  const invitationAcceptCallback = useCallback(() => {
    mutateChecklists();
    mutateDeals();
  }, [mutateChecklists, mutateDeals]);

  return !user?.company_is_paying_customer ? (
    <Paper sx={{ padding: theme.spacing(0, 4, 1, 4) }}>
      <PageHeader
        title={
          <>
            <Text variant="label" fontWeight={500} color={colors.gray80}>
              builders patch
            </Text>
            <Text variant="h2">Collaborator’s dashboard</Text>
          </>
        }
      />

      <Stack padding={theme.spacing(3.5, 2.5)} gap={6}>
        <BookADemoBox />

        <Stack gap={2} data-testid="applications-section">
          <Stack
            direction="row"
            paddingBottom={1}
            justifyContent="space-between"
            sx={{ borderBottom: `1px solid ${colors.blue40}` }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Text variant="h3">Applications</Text>
              {!_.isNil(applications) ? (
                <Text variant="text3" color={colors.gray80}>
                  {applications.length} application
                  {applications.length > 1 ? "s" : ""} accessible to you
                </Text>
              ) : (
                <Skeleton height={21} width={182} />
              )}
            </Stack>

            <Stack direction="row" alignItems="center" gap={1}>
              <Text fontWeight={500} variant="text3">
                Include submitted
              </Text>
              <Switch
                size="small"
                value={includeSubmittedApplications}
                onChange={handleOnIncludeSubmittedApplicationsClick}
                defaultChecked
              />
            </Stack>
          </Stack>

          <Stack gap={2} display="grid" gridTemplateColumns="repeat(4, 1fr)">
            {_.isNil(shownApplications)
              ? _.range(4).map((index) => (
                  <Skeleton variant="rectangular" key={index} height={142} />
                ))
              : shownApplications.map((application) => (
                  <ApplicationBox
                    application={application}
                    key={application.id}
                  />
                ))}
          </Stack>
        </Stack>

        <Stack gap={2} data-testid="checklists-section">
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ borderBottom: `1px solid ${colors.blue40}` }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Text variant="h3">Checklists</Text>
              {!_.isNil(checklists) ? (
                <Text variant="text3" color={colors.gray80}>
                  {checklists.length} checklist
                  {checklists.length > 1 ? "s" : ""} shared with you
                </Text>
              ) : (
                <Skeleton height={21} width={182} />
              )}
            </Stack>
          </Stack>

          <Stack gap={2} display="grid" gridTemplateColumns="repeat(4, 1fr)">
            {areChecklistsLoading
              ? _.range(4).map((index) => (
                  <Skeleton variant="rectangular" key={index} height={157} />
                ))
              : checklists?.map((checklist) => (
                  <ChecklistBox checklist={checklist} key={checklist.id} />
                ))}
          </Stack>
        </Stack>

        {_.isArray(memoSignatures) && memoSignatures.length > 0 && (
          <Stack gap={2}>
            <Stack
              direction="row"
              justifyContent="space-between"
              sx={{ borderBottom: `1px solid ${colors.blue40}` }}
            >
              <Stack direction="row" alignItems="center" gap={2}>
                <Text variant="h3">E-sign documents</Text>
                <Text variant="text3" color={colors.gray80}>
                  {shownMemoSignatures?.length} e-sign document
                  {Number(shownMemoSignatures?.length) > 1 ? "s" : ""} shared
                  with you
                </Text>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                <Text fontWeight={500} variant="text3">
                  Include signed
                </Text>
                <Switch
                  size="small"
                  value={includeSignedMemoSignatures}
                  onChange={handleOnIncludeSignedMemoSignaturesClick}
                  defaultChecked
                />
              </Stack>
            </Stack>

            <Stack gap={2} display="grid" gridTemplateColumns="repeat(4, 1fr)">
              {shownMemoSignatures?.map((memoSignature) => (
                <MemoSignatureBox
                  memoSignature={memoSignature}
                  key={memoSignature.id}
                />
              ))}
            </Stack>
          </Stack>
        )}

        <Stack gap={2} data-testid="deals-section">
          <Stack
            direction="row"
            justifyContent="space-between"
            sx={{ borderBottom: `1px solid ${colors.blue40}` }}
          >
            <Stack direction="row" alignItems="center" gap={2}>
              <Text variant="h3">Deals</Text>
              {!_.isNil(deals) ? (
                <Text variant="text3" color={colors.gray80}>
                  {`${deals.length} deal${deals.length > 1 ? "s" : ""} shared with you`}
                </Text>
              ) : (
                <Skeleton height={21} width={182} />
              )}
            </Stack>
          </Stack>

          {_.isNil(deals) ? (
            <Stack direction="row" gap={2}>
              {_.range(4).map((index) => (
                <Skeleton
                  key={index}
                  variant="rectangular"
                  height="250px"
                  width="100%"
                />
              ))}
            </Stack>
          ) : (
            <DealsGrid
              rows={!_.isNil(deals) ? deals : []}
              showLoading={areDealsLoading}
              isValidating={areChecklistsValidating}
            />
          )}
        </Stack>
      </Stack>
      <DealAndChecklistPendingInvitationsDialog
        invitationAcceptCallback={invitationAcceptCallback}
      />
    </Paper>
  ) : (
    <Navigate to={URLS.NOT_FOUND} />
  );
};
export default CollaboratorDashboard;
